import { Table, Title, Loader } from '@mantine/core';
import { Language } from '../../../languages/languageHandler';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Transaction } from '../../../../libraries/transactions';
import { daysOrMinutesAgo, formatCurrAmount, formatShortTransactionType } from '../../../utils/script';
import { ICurrency } from '../../../../libraries/currencies';
import { IEquipmentDetails } from '../../../../libraries/equipment';
import { formatDateTimeLabel, formatDateTimeNSecLabel } from '../../../utils/date';
import './styles.scss';

type MachineTableProps = {
  list?: IEquipmentDetails[];
  title: string;
  loading?: boolean;
  noResults?: boolean;
};

export function MachineTable({ list, title, loading, noResults }: MachineTableProps) {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  return (
    <div className="machine-table">
      <Title order={3} mb="sm">
        {title}
      </Title>
      <Table.ScrollContainer minWidth={600}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              {[
                'EID',
                `${lang.EQUIPMENT_LOCAL}`,
                `${lang.DASHBOARD_OCCUPANCY}`,
                `${lang.GLOBAL_AMOUNT}`,
                `${lang.DASHBOARD_LAST_TRANSACTION_SHORT}`
              ].map(header => (
                <Table.Th key={header}>{header}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            )}
            {!loading && noResults && (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            )}

            {!loading &&
              !noResults &&
              list?.map(equi => {
                return (
                  <Table.Tr key={equi.equipment.eid}>
                    <Table.Td>{equi.equipment.eid}</Table.Td>
                    <Table.Td>{equi?.location || ''}</Table.Td>
                    <Table.Td>{equi.safeOccPerc}%</Table.Td>
                    <Table.Td>{equi?.amount || ''}</Table.Td>
                    <Table.Td>{formatDateTimeLabel(equi.lastOperationDt)}</Table.Td>
                    <Table.Td>{daysOrMinutesAgo(equi.lastOperationDt, lang)}</Table.Td>
                  </Table.Tr>
                );
              })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  );
}

type OperationListProps = {
  operations: Transaction[];
  loading?: boolean;
  noResults?: boolean;
};

export function OperationList({ operations, loading, noResults }: OperationListProps) {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);

  return (
    <div className="operation-table">
      <Title order={3} mb="sm">
        {lang.DASHBOARD_LAST_OPERATIONS}
      </Title>
      <Table.ScrollContainer minWidth={600}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              {[
                `${lang.GLOBAL_DATE_TIME}`,
                `${lang.GLOBAL_EQUIPMENT}`,
                `${lang.GLOBAL_TYPE}`,
                `${lang.GLOBAL_AMOUNT}`
              ].map(header => (
                <Table.Th key={header}>{header}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && (
              <Table.Tr>
                <Table.Td colSpan={4} style={{ textAlign: 'center' }}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            )}
            {!loading && noResults && (
              <Table.Tr>
                <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            )}

            {!loading &&
              !noResults &&
              operations.map((transaction, i) => {
                const matchingCurrency = currencies?.find(c => c.cid === transaction?.cid);
                return (
                  <Table.Tr key={i}>
                    <Table.Td>{formatDateTimeNSecLabel(transaction.dateTime)}</Table.Td>
                    <Table.Td>{transaction.eid}</Table.Td>
                    <Table.Td>{formatShortTransactionType(lang, transaction.type)}</Table.Td>
                    <Table.Td>
                      {matchingCurrency
                        ? formatCurrAmount(transaction.value || 0, matchingCurrency)
                        : transaction.value}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  );
}
